<template>
  <div>
    <BobPageTitle :config="pageConfig" />
    <BaseTable :data="tableData">
      <BobTableColumn v-for="column in displayTableColumns" :key="column.prop" :column="column" />
    </BaseTable>

    <WarningDialog
      v-if="modal.warning"
      hideCancel
      :content="$t('member.tab.vehicleRecord.unbindConfirm')"
      :confirmBtnLoading="loading.unbinding"
      @close="modal.warning = false"
      @confirm="onConfirmUnbinding"
    />
  </div>
</template>

<script>
import { GetKeeperVehicleListByMember, UnbindMemberKeeperVehicle } from '@/api/keeper'
import BobPageTitle from '@/components/BobPageTitle.vue'
import BobTableColumn from '@/components/BobTableColumn.vue'
import WarningDialog from '@/components/Dialog/WarningDialog.vue'
import { useShop } from '@/use/shop'
import { get } from 'lodash'
import { computed, defineComponent, onBeforeMount, reactive, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router/composables'
import { i18n } from '@/plugins/i18n/i18n'

export default defineComponent({
  name: 'VehicleRecordTab',
  components: {
    BobPageTitle,
    BobTableColumn,
    WarningDialog,
  },
  setup (props, { emit }) {
    const router = useRouter()
    const route = useRoute()
    const { shopId } = useShop()
    const tableData = ref([])
    const selectRow = ref(null)
    const modal = reactive({
      warning: false,
    })
    const loading = reactive({
      unbinding: false,
    })
    const memberId = computed(() => {
      return get(route, 'params.id')
    })
    const pageConfig = computed(() => {
      const count = tableData.value?.length || 0
      const reachedLimit = count >= 50
      return {
        title: i18n.t('member.tab.vehicleRecord.title'),
        actions: [
          {
            key: 'add',
            component: () => import('@/components/BaseElButton.vue'),
            render: () => i18n.t('member.tab.vehicleRecord.add'),
            props: {
              type: 'primary',
              disabled: reachedLimit,
              title: reachedLimit ? i18n.t('member.tab.vehicleRecord.reachedLimit') : '',
            },
            on: {
              click: () => {
                if (reachedLimit) return
                router.push({ name: 'EditMemberVehicle', params: { memberId: memberId.value } })
              },
            },
          },
        ],
      }
    })

    const displayTableColumns = computed(() => {
      const columns = [
        // 車輛編號
        {
          prop: 'vehicleId',
          label: i18n.t('member.tab.vehicleRecord.vehicleId'),
          width: 150,
          render: (row) => {
            return get(row, 'code', '-')
          },
        },
        // 車牌號碼
        {
          prop: 'licensePlate',
          label: i18n.t('member.tab.vehicleRecord.licensePlate'),
          width: 150,
          render: (row) => {
            return get(row, 'plate', '-')
          },
        },
        // 品牌
        {
          prop: 'brand',
          label: i18n.t('member.tab.vehicleRecord.brand'),
          render: (row) => {
            return get(row, 'brandName', '-')
          },
        },
        // 型號
        {
          prop: 'model',
          label: i18n.t('member.tab.vehicleRecord.model'),
          render: (row) => {
            return get(row, 'typeName', '-')
          },
        },
        // 尺寸
        {
          prop: 'size',
          label: i18n.t('member.tab.vehicleRecord.size'),
          render: (row) => {
            return get(row, 'size', '-')
          },
        },
        // 顏色
        {
          prop: 'color',
          label: i18n.t('member.tab.vehicleRecord.color'),
          render: (row) => {
            return get(row, 'info.color', '-')
          },
        },
        // 電瓶
        {
          prop: 'powerSupply',
          label: i18n.t('member.tab.vehicleRecord.battery'),
          render: (row) => {
            return get(row, 'info.battery', '-')
          },
        },
        // 年份
        {
          prop: 'year',
          label: i18n.t('member.tab.vehicleRecord.year'),
          render: (row) => {
            return get(row, 'info.year', '-')
          },
        },
        // 輪框
        {
          prop: 'rim',
          label: i18n.t('member.tab.vehicleRecord.rim'),
          render: (row) => {
            return get(row, 'info.rims', '-')
          },
        },
        // 里程數
        {
          prop: 'mileage',
          label: i18n.t('member.tab.vehicleRecord.mileage'),
          render: (row) => {
            return get(row, 'info.mileage', '-')
          },
        },
        // 操作
        {
          prop: 'operate',
          label: i18n.t('member.common.action.text'),
          fixed: 'right',
          width: 150,
          component: () => import('@/components/OperateColumn.vue'),
          props: (row) => ({
            items: [
              {
                key: 'edit',
                component: () => import('@/components/BaseElButton.vue'),
                render: () => i18n.t('member.tab.vehicleRecord.edit'),
                props: {
                  type: 'text',
                  class: ['underline'],
                },
                on: {
                  click: () => {
                    router.push({
                      name: 'EditMemberVehicle',
                      params: { memberId: memberId.value, code: row.code },
                    })
                  },
                },
              },
              {
                key: 'unBinding',
                component: () => import('@/components/BaseElButton.vue'),
                render: () => i18n.t('member.tab.vehicleRecord.unbind'),
                props: {
                  type: 'text',
                  class: ['underline', 'delete-btn'],
                },
                on: {
                  click: () => {
                    selectRow.value = row
                    modal.warning = true
                  },
                },
              },
            ],
          }),
        },
      ]
      return columns
    })

    const onConfirmUnbinding = async () => {
      if (loading.unbinding) return
      loading.unbinding = true
      const [, errMsg] = await UnbindMemberKeeperVehicle({
        shopId: shopId.value,
        memberId: memberId.value,
        vehicleId: selectRow.value.id,
      })

      loading.unbinding = false

      if (errMsg) {
        window.$message.error(errMsg)
        return
      }

      window.$message.success(i18n.t('member.tab.vehicleRecord.unbindSuccess'))
      modal.warning = false
      refresh()
    }

    const refresh = async () => {
      const [res, errMsg] = await GetKeeperVehicleListByMember({
        shopId: shopId.value,
        memberId: memberId.value,
      })

      if (errMsg) {
        window.$message.error(errMsg)
        return
      }

      tableData.value = get(res, 'keeperVehicles', [])
    }

    onBeforeMount(() => {
      refresh()
    })

    return { pageConfig, displayTableColumns, refresh, tableData, modal, selectRow, loading, onConfirmUnbinding }
  },
})
</script>

<style lang="postcss" scoped></style>
